import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Eye, EyeOff, Loader2, Shield, ArrowLeft, BarChart3, Clock, Target, Lock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SocialLinks from '../components/SocialLinks';
import { getAttribution } from '../utils/visitTracker';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fintech-notify-check/artifacts/2hk0onm3_My%20Direct%20debit%20reminder%20app%20logo.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [source, setSource] = useState(null);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login, register } = useAuth();
  const [searchParams] = useSearchParams();

  // Math challenge state
  const [challenge, setChallenge] = useState(null);
  const [challengeAnswer, setChallengeAnswer] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  // Get source from URL or localStorage
  useEffect(() => {
    const sourceParam = searchParams.get('source');
    if (sourceParam) {
      localStorage.setItem('mdd_source', sourceParam);
      localStorage.setItem('mdd_source_url', window.location.href);
      localStorage.setItem('mdd_source_timestamp', new Date().toISOString());
      setSource(sourceParam);
    } else {
      const storedSource = localStorage.getItem('mdd_source');
      if (storedSource) {
        setSource(storedSource);
      }
    }
  }, [searchParams]);

  // Fetch math challenge when switching to signup
  useEffect(() => {
    if (!isLogin) {
      fetchChallenge();
    }
  }, [isLogin]);

  const fetchChallenge = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/math-challenge`);
      setChallenge(data);
      setChallengeAnswer('');
    } catch {
      // Silently fail — registration will still work without challenge
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(formData.email, formData.password);
      } else {
        if (!formData.name.trim()) {
          setError('Please enter your name');
          setLoading(false);
          return;
        }
        if (challenge && !challengeAnswer.trim()) {
          setError('Please answer the security question');
          setLoading(false);
          return;
        }
        const sourceUrl = localStorage.getItem('mdd_source_url');
        const attribution = getAttribution();
        result = await register(
          formData.name, formData.email, formData.password,
          source, sourceUrl,
          challenge?.challenge_id, challengeAnswer,
          attribution
        );
        
        if (result.success) {
          localStorage.removeItem('mdd_source');
          localStorage.removeItem('mdd_source_url');
          localStorage.removeItem('mdd_source_timestamp');
        }
        if (!result.success && result.error?.includes('sum again')) {
          fetchChallenge();
        }
      }

      if (!result.success) {
        setError(result.error);
        toast.error(result.error);
      } else {
        toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success('If that email exists, we\'ve sent a reset link.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const formatSourceName = (s) => {
    if (!s) return '';
    return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Forgot password view
  if (showForgot) {
    return (
      <div className="min-h-screen bg-page flex flex-col">
        <header className="px-4 py-6">
          <div className="max-w-md mx-auto flex items-center justify-center">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="My Direct Debits" className="h-12 w-12 object-contain" />
              <div>
                <h1 className="font-heading font-bold text-dark text-xl">My Direct Debits</h1>
                <p className="text-xs text-text-light">Reminder App</p>
              </div>
            </Link>
          </div>
        </header>
        <main className="flex-1 px-4 py-8">
          <div className="max-w-md mx-auto">
            <div className="card-default p-8">
              <button onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }} className="text-text-secondary hover:text-primary mb-4 flex items-center gap-1 text-sm">
                <ArrowLeft className="w-4 h-4" /> Back to Sign In
              </button>
              <h2 className="font-heading font-bold text-dark text-2xl text-center mb-2">Forgot Password</h2>
              <p className="text-text-secondary text-center mb-8 text-sm">Enter your email and we'll send you a link to reset your password.</p>

              {forgotSent ? (
                <div className="bg-primary-light text-primary-dark p-4 rounded-xl text-center" data-testid="forgot-success">
                  <p className="font-semibold mb-1">Check your email</p>
                  <p className="text-sm">If an account exists for <strong>{forgotEmail}</strong>, we've sent a reset link. It expires in 1 hour.</p>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Email Address</label>
                    <Input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="Enter your email"
                      data-testid="forgot-email-input"
                      className="input-field w-full"
                      required
                    />
                  </div>
                  <Button type="submit" disabled={forgotLoading} className="btn-primary w-full py-3 text-base" data-testid="forgot-submit-btn">
                    {forgotLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Send Reset Link'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page flex flex-col">
      {/* Recommended by banner */}
      {source && (
        <div className="bg-primary-light border-b border-primary/20 px-4 py-2">
          <p className="text-center text-sm text-primary-dark">
            Recommended by <span className="font-semibold">{formatSourceName(source)}</span>
          </p>
        </div>
      )}
      
      {/* Header */}
      <header className="px-4 py-6">
        <div className="max-w-md mx-auto flex items-center justify-center">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="My Direct Debits" className="h-12 w-12 object-contain" />
            <div>
              <h1 className="font-heading font-bold text-dark text-xl">My Direct Debits</h1>
              <p className="text-xs text-text-light">Reminder App</p>
            </div>
          </Link>
        </div>
      </header>

      {/* Auth Form */}
      <main className="flex-1 px-4 py-8">
        <div className="max-w-md mx-auto">
          <div className="card-default p-8">
            <h2 className="font-heading font-bold text-dark text-2xl text-center mb-2">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-text-secondary text-center mb-8">
              {isLogin ? 'Sign in to continue' : 'Sign up to get started'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Full Name
                  </label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your name"
                    data-testid="name-input"
                    className="input-field w-full"
                    required={!isLogin}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter your email"
                  data-testid="email-input"
                  className="input-field w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter your password"
                    data-testid="password-input"
                    className="input-field w-full pr-12"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-light hover:text-text-secondary"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="text-primary text-sm mt-2 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                )}
              </div>

              {/* Math challenge - signup only */}
              {!isLogin && challenge && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Quick check: What is <span className="font-bold text-primary">{challenge.question}</span>?
                  </label>
                  <Input
                    type="number"
                    value={challengeAnswer}
                    onChange={(e) => setChallengeAnswer(e.target.value)}
                    placeholder="Your answer"
                    data-testid="math-challenge-input"
                    className="input-field w-full"
                    required
                  />
                </div>
              )}

              {error && (
                <div className="bg-alert-light text-alert text-sm p-3 rounded-lg" data-testid="auth-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-btn"
                className="btn-primary w-full py-3 text-base"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  isLogin ? 'Sign In' : 'Sign Up'
                )}
              </Button>
            </form>

            {isLogin ? (
              <>
                {/* OR divider */}
                <div className="flex items-center gap-3 my-5" aria-hidden="true">
                  <span className="flex-1 h-px bg-border" />
                  <span className="text-xs font-semibold text-text-light tracking-wider">OR</span>
                  <span className="flex-1 h-px bg-border" />
                </div>

                {/* Prominent yellow "New Here?" CTA */}
                <button
                  onClick={() => {
                    setIsLogin(false);
                    setError('');
                  }}
                  data-testid="signup-cta-btn"
                  className="w-full py-3.5 rounded-xl font-heading font-bold text-dark text-base flex items-center justify-center gap-3 shadow-sm transition-colors"
                  style={{ background: '#FCD34D' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#F59E0B')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#FCD34D')}
                >
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-dark text-white">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                  New Here? Create Your Free Account
                </button>

                {/* Free Spending Insight Report promo card */}
                <div className="mt-5 bg-primary-light/60 border border-primary/20 rounded-2xl p-5" data-testid="spending-report-card">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <BarChart3 className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-heading font-bold text-dark text-base sm:text-lg">
                      Get My Free Spending Insight Report
                    </h3>
                  </div>
                  <p className="text-text-secondary text-sm mb-4 leading-relaxed">
                    Personalised in <strong className="text-primary">under 60 seconds</strong> • No card or bank connection required
                  </p>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="bg-white rounded-lg p-2.5 text-center">
                      <Clock className="w-4 h-4 text-primary mx-auto mb-1" />
                      <p className="text-[11px] text-text-secondary font-medium leading-tight">Takes less than 60 seconds</p>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 text-center">
                      <Target className="w-4 h-4 text-primary mx-auto mb-1" />
                      <p className="text-[11px] text-text-secondary font-medium leading-tight">Discover what's really leaving your account</p>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 text-center">
                      <Lock className="w-4 h-4 text-primary mx-auto mb-1" />
                      <p className="text-[11px] text-text-secondary font-medium leading-tight">100% Free. No bank connection</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsLogin(false);
                      setError('');
                    }}
                    data-testid="signup-report-cta-btn"
                    className="btn-primary w-full py-3 text-sm sm:text-base font-semibold flex items-center justify-center gap-2"
                  >
                    Get My Free Spending Insight Report
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setIsLogin(false);
                      setError('');
                    }}
                    data-testid="signup-pilot-cta-btn"
                    className="mt-3 w-full py-3 rounded-xl font-heading font-bold text-sm sm:text-base flex items-center justify-center gap-2 transition-colors"
                    style={{ background: '#FFFFFF', color: '#DB2777', border: '2px solid #DB2777' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#DB2777'; e.currentTarget.style.color = '#FFFFFF'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.color = '#DB2777'; }}
                  >
                    Sign Up to Pilot
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </>
            ) : (
              <p className="text-center text-text-secondary text-sm mt-6">
                Already have an account?{' '}
                <button
                  onClick={() => {
                    setIsLogin(true);
                    setError('');
                  }}
                  data-testid="toggle-auth-mode"
                  className="text-primary font-semibold hover:underline"
                >
                  Sign In
                </button>
              </p>
            )}

            <p className="text-center text-text-light text-xs mt-6">
              By continuing, you agree to our{' '}
              <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
            </p>
          </div>

          {/* Security & Trust - shown on sign up */}
          {!isLogin && (
            <div className="mt-6 bg-primary-light rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    We only collect the information needed to manage your account. <strong className="text-dark">We do not collect or store your bank details or card details.</strong> Payments are processed securely by Stripe.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="px-4 py-4 text-center">
        <div className="mb-2">
          <SocialLinks />
        </div>
        <p className="text-text-light text-xs">
          Voliciti Group Ventures Ltd
        </p>
      </footer>
    </div>
  );
}
